import { useMemo, useCallback, useState, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { AppNavigationContext } from '../context/AppNavigationContext';

import { ROUTE_PATH } from '../route/utils';
import { trackPixel } from '../utils/analytics/metaPixel';
import { getFromLocalStorage, STORAGE_KEYS } from '../utils/storage/storage';

// public/index.html already fires PageView for the page the app loaded on
let lastTrackedPath = window.location.pathname;

const AppNavigationProvider = ({ children }) => {
  const location = useLocation();
  const [showCreateMealModal, setShowCreateMealModal] = useState(false);
  const [showMealPlannerModal, setShowMealPlannerModal] = useState(false);
  const [showMealChoiceModal, setShowMealChoiceModal] = useState(false);
  const [showOrderStatusModal, setShowOrderStatusModal] = useState(false);
  const [orderStatus, setOrderStatus] = useState(null);
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  useEffect(() => {
    // This provider remounts on every route, so the last tracked path lives at module level
    if (location.pathname === lastTrackedPath) return;
    lastTrackedPath = location.pathname;
    trackPixel('PageView', { page_path: location.pathname });
  }, [location.pathname]);

  useEffect(() => {
    const order = searchParams.get('order');

    if (order && !showOrderStatusModal) {
      const success = searchParams.get('success') === 'true';

      if (success) {
        const pending = getFromLocalStorage(STORAGE_KEYS.PENDING_PURCHASE);
        if (pending && String(pending.orderNumber) === order) {
          // eventID lets a future server-side Conversions API event dedupe against this one
          trackPixel(
            'Purchase',
            { value: pending.value, currency: 'ZAR', num_items: pending.numItems },
            { eventID: order }
          );
          localStorage.removeItem(STORAGE_KEYS.PENDING_PURCHASE);
        }
      }

      setOrderStatus({
        order,
        success
      });
      setShowOrderStatusModal(!!order);
      navigate('/');
    }
    //eslint-disable-next-line
  }, [searchParams, showOrderStatusModal]);

  const isCurrentRoute = useCallback(
    (route, startWith = false) => {
      return startWith ? location.pathname.startsWith(route) : location.pathname === route;
    },
    [location]
  );

  const showSearch = useMemo(() => {
    return isCurrentRoute(ROUTE_PATH.MENU);
  }, [isCurrentRoute]);

  const memoizedAppHeader = useMemo(() => {
    return isCurrentRoute(ROUTE_PATH.MENU) || isCurrentRoute(ROUTE_PATH.MENU_ITEM, true);
  }, [isCurrentRoute]);

  return (
    <AppNavigationContext.Provider
      value={{
        isCurrentRoute,
        showAppHeader: memoizedAppHeader,
        showCreateMealModal,
        setShowCreateMealModal,
        showMealPlannerModal,
        setShowMealPlannerModal,
        showMealChoiceModal,
        setShowMealChoiceModal,
        showOrderStatusModal,
        setShowOrderStatusModal,
        orderStatus,
        showSearch
      }}
    >
      {children}
    </AppNavigationContext.Provider>
  );
};

export default AppNavigationProvider;
